import React, { useContext } from 'react';
import type { FrequencyEnum } from 'venn-api';
import { EmptyStateContent } from '../components/styled';
import { isPeriodSet, getPeriodFormatterForInsufficientReturns } from '../PortfolioRangesUtils';
import type { AnalysisSubject, TimeFrame } from 'venn-utils';
import { getFormattedPeriod, MANAGE_DATA_SECTION } from 'venn-utils';
import type { RangeDebugGroup } from '../../../hooks/useRangesDebug';
import ErrorInvalidAnalysisPeriod from '../shared-errors/ErrorInvalidAnalysisPeriod';
import { Button, getTextThemeProvider } from 'venn-ui-kit';
import { ManageDataLink } from '../../../manage-data';
import FactorLensesContext from '../../../contexts/factor-lenses-context';

interface RangeMetadata {
  availableStart: number;
  availableEnd: number;
  frequency: FrequencyEnum;
  requiredMonths: number;
  analysisMonths: number;
  requiredLabel: string;
}

interface ErrorInsufficientReturnsProps {
  defaultMessage: string | null;
  rangeMetadata: RangeMetadata;
  subject: AnalysisSubject;
  regressionName: string;
  onResetAnalysisPeriod?: () => void;
  actualTimeFrame: TimeFrame;
  rangeDebugGroup?: RangeDebugGroup;
  isFactorTrend?: boolean;
}

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
export default ({
  subject,
  rangeDebugGroup,
  onResetAnalysisPeriod,
  defaultMessage,
  rangeMetadata,
  regressionName,
  actualTimeFrame,
  isFactorTrend,
}: ErrorInsufficientReturnsProps) => {
  const { primaryFactorLens } = useContext(FactorLensesContext);
  const { availableStart, availableEnd, frequency, requiredLabel } = rangeMetadata;
  const noOverlap = isPeriodSet(availableStart, availableEnd) && availableStart > availableEnd;

  const portfolioRange = rangeDebugGroup?.primary?.response?.portfolioRange;

  if (!portfolioRange) {
    return <EmptyStateContent>{defaultMessage}</EmptyStateContent>;
  }

  const actualStart = actualTimeFrame.startTime ?? availableStart;
  const actualEnd = actualTimeFrame.endTime ?? availableEnd;

  const formatDateRange = getPeriodFormatterForInsufficientReturns(frequency, availableStart, availableEnd, noOverlap);
  const availableRange = formatDateRange(availableStart, availableEnd);

  if (
    isPeriodSet(availableStart, availableEnd, isFactorTrend) &&
    ((!isFactorTrend && actualStart !== availableStart) ||
      (actualEnd !== availableEnd && actualEnd !== primaryFactorLens?.earliestEndDate))
  ) {
    return (
      <ErrorInvalidAnalysisPeriod
        buttonTitle={`Reset Date To ${getTextThemeProvider().factorLensShortName} History`}
        defaultMessage="Subject has insufficient returns or the selected analysis period is not supported by this block. Adjust the analysis period to run analysis."
        onResetAnalysisPeriod={onResetAnalysisPeriod}
      />
    );
  }

  // @ts-expect-error: fixme
  const customRenderer = (onClick) => (
    <div>
      <p>
        {subject.type === 'portfolio' ? (
          <span>
            <strong>Remove</strong> investments with insufficient returns from your portfolio or go to
          </span>
        ) : (
          <span>Go to</span>
        )}{' '}
        <button type="button" onClick={onClick} className="text-venn-dark-blue hover:text-venn-dark-blue/90 font-bold">
          Manage Data
        </button>{' '}
        to apply proxies.
      </p>
      <Button className="print:hidden" onClick={onClick} dominant refreshedStyling>
        Manage Data
      </Button>
    </div>
  );

  return (
    <div>
      <div className="text-[16px] text-balance">
        <p className="mt-0">
          {!noOverlap && (
            <>
              <strong>{regressionName}</strong> requires at least <strong>{requiredLabel}</strong> of return history for{' '}
              <span className="font-bold lowercase">{frequency}</span> analysis.{' '}
            </>
          )}
          {noOverlap ? (
            <>
              Currently, the return streams of the items you're analyzing have <strong>no common overlap</strong>.
            </>
          ) : (
            <>
              Currently, the return streams of the items you're analyzing overlap over{' '}
              <strong>{getFormattedPeriod(frequency, availableStart, availableEnd)}</strong> (
              <strong>{availableRange}</strong>).
            </>
          )}
        </p>
        {/* TODO(houssein): Refactor ManageDataLink to a custom navigation hook */}
        <ManageDataLink
          subject={subject}
          defaultEditData
          customRenderer={customRenderer}
          forceSection={MANAGE_DATA_SECTION.ANALYSIS_RANGE}
          errorState
        />
      </div>
    </div>
  );
};
